import { useEffect, useState } from "react";
import { toast } from "sonner";
import api from "../lib/api";
import { useJobsite } from "../context/JobsiteContext";
import { useAuth } from "../context/AuthContext";
import { PageHeader, EmptyState } from "../components/app/Common";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Dialog, DialogContent, DialogTitle } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { fmtDate, fmtTime, durationMinutes, hoursMinutes, requestGeo } from "../lib/utils";
import { Clock, Play, Square, Coffee, MapPin, CheckCircle2, XCircle, Search, ChevronRight, X, Eye } from "lucide-react";
import { MapContainer, TileLayer, Marker, Popup } from "react-leaflet";
import L from "leaflet";

// Correção padrão para o ícone do marcador do Leaflet no React
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
});

export default function Attendance() {
  const { activeSite, sites, setActiveSite } = useJobsite();
  const { user } = useAuth();
  const [shift, setShift] = useState(null);
  const [history, setHistory] = useState([]);
  const [busy, setBusy] = useState(false);

  // Estados para o Modal de Timecard Details
  const [showClockInModal, setShowClockInModal] = useState(false);
  const [lunchTime, setLunchTime] = useState("30");
  const [description, setDescription] = useState("");
  const [openProjectSearch, setOpenProjectSearch] = useState(false);

  // Estado para o Modal do Mapa do Turno Selecionado
  const [selectedShiftForMap, setSelectedShiftForMap] = useState(null);

  const load = async () => {
    const [cur, hist] = await Promise.all([
      api.get("/attendance/current"),
      api.get("/attendance/history?limit=20"),
    ]);
    setShift(cur.data);
    setHistory(hist.data);
  };

  useEffect(() => { load(); }, []);

  const handleClockInSubmit = async () => {
    if (!activeSite) {
      toast.error("Select a jobsite first");
      return;
    }
    setBusy(true);
    try {
      const geo = await requestGeo();
      const { data } = await api.post("/attendance/clock-in", {
        jobsite_id: activeSite.id, 
        lat: geo.lat, 
        lng: geo.lng,
        lunch_minutes: parseInt(lunchTime),
        description
      });
      toast.success("Clocked in");
      setShift(data);
      setShowClockInModal(false);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Action failed");
    } finally { setBusy(false); }
  };

  const doClockOut = async () => {
    setBusy(true);
    try {
      const geo = await requestGeo();
      await api.post("/attendance/clock-out", {
        jobsite_id: activeSite?.id, lat: geo.lat, lng: geo.lng,
      });
      toast.success("Clocked out");
      setShift(null);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Action failed");
    } finally { setBusy(false); }
  };

  const doLunch = async (action) => {
    setBusy(true);
    try {
      const { data } = await api.post("/attendance/lunch", { action });
      setShift(data);
      toast.success(action === "start" ? "Lunch started" : "Lunch ended");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Action failed");
    } finally { setBusy(false); }
  };

  const activeMins = shift ? durationMinutes(shift.clock_in) : 0;
  const lunchMins = shift?.lunch_start ? durationMinutes(shift.lunch_start, shift.lunch_end) : 0;

  return (
    <div className="p-6 md:p-10 max-w-6xl mx-auto">
      <PageHeader
        title="Attendance"
        subtitle="GPS-verified clock in/out. Lunch breaks tracked separately."
      />

      {/* Live shift */}
      <div className="border border-border bg-card rounded-sm p-6 mb-8" data-testid="attendance-current">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <div className="overline mb-1">CURRENT SHIFT</div>
            <div className="flex items-center gap-3">
              <Clock className="h-6 w-6 text-primary" />
              <div className="metric text-4xl">{hoursMinutes(activeMins)}</div>
              {shift && (
                <Badge variant={shift.within_geofence ? "default" : "destructive"} className="rounded-sm font-mono uppercase tracking-widest">
                  {shift.within_geofence ? "GEO OK" : "OFF-SITE"}
                </Badge>
              )}
            </div>
            <div className="text-xs text-muted-foreground font-mono mt-2">
              {shift
                ? `Started ${fmtTime(shift.clock_in)} at ${shift.jobsite_name} · ${shift.in_distance_m?.toFixed?.(1) ?? shift.in_distance_m}m from center`
                : "Not clocked in."}
            </div>
          </div>
          <div className="flex flex-wrap gap-2">
            {!shift ? (
              <Button
                disabled={busy || !activeSite}
                onClick={() => setShowClockInModal(true)}
                className="rounded-sm bg-primary hover:bg-primary/90 h-11"
                data-testid="clock-in-btn"
              >
                <Play className="h-4 w-4 mr-1.5" /> Clock in
              </Button>
            ) : (
              <>
                {!shift.lunch_start && (
                  <Button variant="outline" disabled={busy} onClick={() => doLunch("start")} className="rounded-sm border-border h-11">
                    <Coffee className="h-4 w-4 mr-1.5" /> Start lunch
                  </Button>
                )}
                {shift.lunch_start && !shift.lunch_end && (
                  <Button variant="outline" disabled={busy} onClick={() => doLunch("end")} className="rounded-sm border-border h-11">
                    <Coffee className="h-4 w-4 mr-1.5" /> End lunch
                  </Button>
                )}
                <Button variant="destructive" disabled={busy} onClick={doClockOut} className="rounded-sm h-11">
                  <Square className="h-4 w-4 mr-1.5" /> Clock out
                </Button>
              </>
            )}
          </div>
        </div>

        {activeSite && (
          <div className="mt-6 pt-4 border-t border-border flex items-center gap-2 text-xs text-muted-foreground">
            <MapPin className="h-3.5 w-3.5" />
            <span className="font-mono">
              Site: {activeSite.name} · geofence {activeSite.radius_m}m ·
              lat {activeSite.lat?.toFixed?.(4)} lng {activeSite.lng?.toFixed?.(4)}
            </span>
          </div>
        )}
      </div>

      {/* Modal de Timecard Details */}
      <Dialog open={showClockInModal} onOpenChange={setShowClockInModal}>
        <DialogContent className="rounded-sm bg-card border-border text-card-foreground max-w-lg p-0 overflow-hidden">
          <div className="flex items-center justify-between px-6 py-4 border-b border-border bg-muted/40">
            <Button variant="ghost" size="sm" onClick={() => setShowClockInModal(false)} className="text-muted-foreground hover:text-foreground">
              Cancel
            </Button>
            <DialogTitle className="text-base font-semibold">Timecard details</DialogTitle>
            <Button 
              size="sm" 
              onClick={handleClockInSubmit} 
              disabled={busy || !activeSite}
              className="bg-primary hover:bg-primary/90 text-primary-foreground rounded-sm font-medium"
            >
              {busy ? "Applying..." : "Apply"}
            </Button>
          </div>

          <div className="p-6 space-y-6">
            <div className="space-y-1.5">
              <Label className="text-xs font-mono uppercase tracking-widest text-muted-foreground">
                Project (required)
              </Label>
              <div 
                onClick={() => setOpenProjectSearch(true)}
                className="w-full min-h-[48px] rounded-sm border border-border bg-background px-3 py-2 flex items-center justify-between cursor-pointer hover:border-primary/50 transition-colors"
              >
                <span className="text-sm font-mono truncate">
                  {activeSite ? activeSite.name : "Select project..."}
                </span>
                <ChevronRight className="h-4 w-4 text-muted-foreground shrink-0 ml-2" />
              </div>
            </div>

            <div className="space-y-1.5">
              <Label className="text-xs font-mono uppercase tracking-widest text-muted-foreground">
                Lunch time
              </Label>
              <Select value={lunchTime} onValueChange={setLunchTime}>
                <SelectTrigger className="w-full rounded-sm border-border bg-background h-12 font-mono">
                  <SelectValue placeholder="Select lunch time" />
                </SelectTrigger>
                <SelectContent className="rounded-sm border-border bg-card">
                  <SelectItem value="0">0 min.</SelectItem>
                  <SelectItem value="15">15 min.</SelectItem>
                  <SelectItem value="30">30 min.</SelectItem>
                  <SelectItem value="45">45 min.</SelectItem>
                  <SelectItem value="60">60 min.</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-1.5">
              <Label className="text-xs font-mono uppercase tracking-widest text-muted-foreground">
                Description
              </Label>
              <Textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Add notes or details..."
                className="rounded-sm border-border bg-background font-mono resize-none h-24"
              />
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Modal de Seleção de Projetos */}
      <Dialog open={openProjectSearch} onOpenChange={setOpenProjectSearch}>
        <DialogContent className="rounded-sm bg-card border-border text-card-foreground max-w-md p-0 overflow-hidden">
          <div className="flex items-center justify-between px-6 py-4 border-b border-border bg-muted/40">
            <DialogTitle className="text-base font-semibold">Select project</DialogTitle>
            <Button variant="ghost" size="icon" onClick={() => setOpenProjectSearch(false)} className="h-8 w-8 rounded-sm">
              <X className="h-4 w-4" />
            </Button>
          </div>
          <div className="p-4 space-y-2 max-h-[60vh] overflow-y-auto">
            {sites.map((site) => (
              <div 
                key={site.id}
                onClick={() => {
                  setActiveSite(site);
                  setOpenProjectSearch(false);
                }}
                className={`p-3 rounded-sm border cursor-pointer transition-all space-y-1 ${activeSite?.id === site.id ? 'border-primary bg-primary/5' : 'border-border bg-background hover:border-primary/50'}`}
              >
                <div className="text-sm font-semibold leading-tight">{site.name}</div>
                <div className="text-xs text-muted-foreground font-mono flex items-center justify-between pt-1">
                  <span>Radius: {site.radius_m}m</span>
                  <span className="flex items-center text-primary font-medium">
                    <MapPin className="h-3 w-3 mr-1" /> Select
                  </span>
                </div>
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>

      {/* Modal para Visualizar o Mapa do Turno */}
      <Dialog open={!!selectedShiftForMap} onOpenChange={() => setSelectedShiftForMap(null)}>
        <DialogContent className="rounded-sm bg-card border-border text-card-foreground max-w-xl p-0 overflow-hidden">
          <div className="flex items-center justify-between px-6 py-4 border-b border-border bg-muted/40">
            <DialogTitle className="text-base font-semibold">Location verification — {selectedShiftForMap?.jobsite_name}</DialogTitle>
            <Button variant="ghost" size="icon" onClick={() => setSelectedShiftForMap(null)} className="h-8 w-8 rounded-sm">
              <X className="h-4 w-4" />
            </Button>
          </div>
          <div className="p-4">
            {selectedShiftForMap?.lat && selectedShiftForMap?.lng ? (
              <div className="h-[350px] w-full rounded-sm overflow-hidden border border-border">
                <MapContainer 
                  center={[selectedShiftForMap.lat, selectedShiftForMap.lng]} 
                  zoom={16} 
                  style={{ height: '100%', width: '100%' }}
                >
                  <TileLayer
                    url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                    attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                  />
                  <Marker position={[selectedShiftForMap.lat, selectedShiftForMap.lng]}>
                    <Popup>
                      <strong>{selectedShiftForMap.jobsite_name}</strong><br />
                      Clock-in location verified via GPS.<br />
                      Distance from center: {selectedShiftForMap.in_distance_m?.toFixed?.(1) || 0}m
                    </Popup>
                  </Marker>
                </MapContainer>
              </div>
            ) : (
              <div className="p-8 text-center text-sm text-muted-foreground">
                No GPS coordinates recorded for this shift.
              </div>
            )}
            <div className="mt-3 text-xs text-muted-foreground font-mono flex justify-between items-center">
              <span>Lat: {selectedShiftForMap?.lat} | Lng: {selectedShiftForMap?.lng}</span>
              <span className={selectedShiftForMap?.within_geofence ? "text-[hsl(var(--status-pass))]" : "text-destructive"}>
                {selectedShiftForMap?.within_geofence ? "Within Geofence Range" : "Outside Geofence Range"}
              </span>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Timesheet history */}
      <div>
        <div className="flex items-end justify-between mb-3">
          <div>
            <div className="overline mb-1">Timesheet</div>
            <h2 className="text-xl font-semibold tracking-tight">Recent shifts</h2>
          </div>
          <div className="text-xs text-muted-foreground font-mono">Last {history.length}</div>
        </div>

        {history.length === 0 ? (
          <EmptyState title="No shifts yet" body="Your clock-ins will appear here." />
        ) : (
          <div className="border border-border rounded-sm overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-muted/40 text-left overline">
                <tr>
                  <Th>Date</Th><Th>Site</Th><Th>In</Th><Th>Out</Th>
                  <Th>Duration</Th><Th>Geo Status</Th><Th>Map</Th><Th>Approved</Th>
                </tr>
              </thead>
              <tbody>
                {history.map((h) => {
                  const mins = h.clock_out ? durationMinutes(h.clock_in, h.clock_out) : durationMinutes(h.clock_in);
                  return (
                    <tr key={h.id} className="border-t border-border hover:bg-accent/30" data-testid={`shift-row-${h.id}`}>
                      <Td mono>{fmtDate(h.clock_in)}</Td>
                      <Td>{h.jobsite_name}</Td>
                      <Td mono>{fmtTime(h.clock_in)}</Td>
                      <Td mono>{h.clock_out ? fmtTime(h.clock_out) : <em className="text-primary">active</em>}</Td>
                      <Td mono>{hoursMinutes(mins)}</Td>
                      <Td>
                        {h.within_geofence ? (
                          <span className="inline-flex items-center gap-1 text-[hsl(var(--status-pass))]"><CheckCircle2 className="h-3.5 w-3.5" /> OK</span>
                        ) : (
                          <span className="inline-flex items-center gap-1 text-destructive"><XCircle className="h-3.5 w-3.5" /> Off</span>
                        )}
                      </Td>
                      <Td>
                        {h.lat && h.lng ? (
                          <Button 
                            size="sm" 
                            variant="outline" 
                            className="h-7 text-xs rounded-sm border-border"
                            onClick={() => setSelectedShiftForMap(h)}
                          >
                            <Eye className="h-3.5 w-3.5 mr-1" /> View Map
                          </Button>
                        ) : (
                          <span className="text-xs text-muted-foreground">—</span>
                        )}
                      </Td>
                      <Td>
                        {h.approved === true && <Badge className="rounded-sm font-mono uppercase tracking-wider">approved</Badge>}
                        {h.approved === null && <span className="text-xs text-muted-foreground font-mono">—</span>}
                        {h.approved === false && <Badge variant="destructive" className="rounded-sm">rejected</Badge>}
                      </Td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

const Th = ({ children }) => <th className="px-4 py-2 font-mono text-[10px] tracking-widest">{children}</th>;
const Td = ({ children, mono }) => <td className={`px-4 py-3 ${mono ? "font-mono text-xs" : ""}`}>{children}</td>;